import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "api/api";
import { loginReset } from "redux/slice/authentication";
import { arrayToById, objectToById } from "redux/normalize";
import { CampaignDisplayCategoryResource } from "api/resource/campaign-display-category";
import { fetchCampaigns } from "redux/slice/campaigns";

export interface CampaignDisplayCategoriesState {
	loading?: boolean;
	error?: string | null;
	byId: {
		[key: number]: CampaignDisplayCategoryResource;
	};
};

const initialState: CampaignDisplayCategoriesState = {
	byId: {},
};

const fetchCampaignDisplayCategories = createAsyncThunk(
	"campaignDisplayCategories/fetchCampaignDisplayCategories",
	async (payload, thunkAPI) => {
		thunkAPI.dispatch(campaignDisplayCategoriesLoading());
		const result = api.getCampaignDisplayCategories();
		result
			.then((categories) => thunkAPI.dispatch(campaignDisplayCategoriesLoaded(categories)))
			.catch((error) => thunkAPI.dispatch(campaignDisplayCategoriesFailed(error)));
		return result;
	},
);

// Add a category. The new row merges into byId; getSortedCampaignDisplayCategories re-sorts, so
// the accordion lands in its alphabetical position automatically (AM3).
const createCampaignDisplayCategory = createAsyncThunk(
	"campaignDisplayCategories/createCampaignDisplayCategory",
	async (payload: { name: string }, thunkAPI) => {
		const result = api.createCampaignDisplayCategory(payload.name);
		result
			.then((category) => thunkAPI.dispatch(campaignDisplayCategoryLoaded(category)))
			.catch((error) => thunkAPI.dispatch(campaignDisplayCategoriesFailed(error)));
		return result;
	},
);

// Rename a category. Merges the renamed row back over its id (name changes → selector re-sorts).
const updateCampaignDisplayCategory = createAsyncThunk(
	"campaignDisplayCategories/updateCampaignDisplayCategory",
	async (payload: { id: number; name: string }, thunkAPI) => {
		const result = api.updateCampaignDisplayCategory(payload.id, payload.name);
		result
			.then((category) => thunkAPI.dispatch(campaignDisplayCategoryLoaded(category)))
			.catch((error) => thunkAPI.dispatch(campaignDisplayCategoriesFailed(error)));
		return result;
	},
);

// Delete (empty) or Move-&-Delete a category (one atomic server op, AR4). On success the row is
// dropped from byId; when campaigns were reassigned (moveToId provided, incl. null=Uncategorized)
// we resync campaigns so the grouped view reflects the moved FKs immediately. Rejection (e.g. the
// 409 last-category guard) surfaces via .unwrap() to the caller and records the error.
const deleteCampaignDisplayCategory = createAsyncThunk(
	"campaignDisplayCategories/deleteCampaignDisplayCategory",
	async (payload: { id: number; moveToId?: number | null }, thunkAPI) => {
		try {
			await api.deleteCampaignDisplayCategory(payload.id, payload.moveToId);
			thunkAPI.dispatch(campaignDisplayCategoryRemoved(payload.id));
			if (payload.moveToId !== undefined) {
				thunkAPI.dispatch(fetchCampaigns());
			}
			return payload.id;
		} catch(error) {
			thunkAPI.dispatch(campaignDisplayCategoriesFailed(error));
			throw error;
		}
	},
);

export const campaignDisplayCategoriesSlice = createSlice({
	name: "campaignDisplayCategories",
	initialState,
	reducers: {
		campaignDisplayCategoriesLoading: (state) => {
			state.loading = true;
		},
		campaignDisplayCategoriesLoaded: (state, action: PayloadAction<CampaignDisplayCategoryResource[]>) => {
			state.loading = false;
			state.error = null;
			state.byId = arrayToById(action.payload, "id");
		},
		campaignDisplayCategoryLoaded: (state, action: PayloadAction<CampaignDisplayCategoryResource>) => {
			const byId = objectToById(action.payload, "id");
			state.loading = false;
			state.error = null;
			state.byId = {
				...state.byId,
				...byId,
			};
		},
		campaignDisplayCategoriesFailed: (state, action: PayloadAction<any>) => {
			state.loading = false;
			state.error = action.payload && action.payload.message;
		},
		campaignDisplayCategoryRemoved: (state, action: PayloadAction<number>) => {
			delete state.byId[action.payload];
			state.error = null;
		},
		default: () => {
			return;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	campaignDisplayCategoriesLoading,
	campaignDisplayCategoriesLoaded,
	campaignDisplayCategoryLoaded,
	campaignDisplayCategoriesFailed,
	campaignDisplayCategoryRemoved,
} = campaignDisplayCategoriesSlice.actions;
export {
	fetchCampaignDisplayCategories,
	createCampaignDisplayCategory,
	updateCampaignDisplayCategory,
	deleteCampaignDisplayCategory,
};
